import { useQuery } from '@tanstack/react-query';
import { teamRepository } from '../repositories/team.repository';
import { orgKeys } from '../services/organization.keys';
import { permissionService } from '../services/permission.service';
import { useCurrentOrganization } from './useCurrentOrganization';
import type { AppRole, UserRole } from '../types/organization.types';

export type { AppRole, UserRole };

export function useUserRole(orgIdOverride?: string) {
  const { data: currentOrg } = useCurrentOrganization();
  const orgId = orgIdOverride ?? currentOrg?.id;

  return useQuery({
    queryKey: orgKeys.userRole(orgId),
    queryFn: async (): Promise<UserRole | null> => {
      if (!orgId) return null;
      return teamRepository.getCurrentUserRole(orgId);
    },
    enabled: !!orgId,
  });
}

export function useCurrentUserRole(orgIdOverride?: string) {
  const { data: currentOrg } = useCurrentOrganization();
  const orgId = orgIdOverride ?? currentOrg?.id;

  return useQuery({
    queryKey: ['current-user-role', orgId],
    queryFn: async (): Promise<string | null> => {
      if (!orgId) return null;
      const userRole = await teamRepository.getCurrentUserRole(orgId);
      return userRole?.role ?? null;
    },
    enabled: !!orgId,
  });
}


export function useHasRole(requiredRole: AppRole): boolean {
  const { data: userRole } = useUserRole();
  return permissionService.hasRole(userRole?.role, requiredRole);
}

export function useHasAnyRole(requiredRoles: AppRole[]): boolean {
  const { data: userRole } = useUserRole();
  return permissionService.hasAnyRole(userRole?.role, requiredRoles);
}

// Role-based permission checks (UI visibility)
export function useCanManageCampaigns(): boolean {
  const { data: userRole } = useUserRole();
  return permissionService.canManageCampaigns(userRole?.role);
}

export function useCanManageInfluencers(): boolean {
  const { data: userRole } = useUserRole();
  return permissionService.canManageInfluencers(userRole?.role);
}

export function useCanManagePayments(): boolean {
  const { data: userRole } = useUserRole();
  return permissionService.canManagePayments(userRole?.role);
}

export function useCanManageBrands(): boolean {
  const { data: userRole } = useUserRole();
  return permissionService.canManageBrands(userRole?.role);
}

export function useCanManageContacts(): boolean {
  const { data: userRole } = useUserRole();
  return permissionService.canManageContacts(userRole?.role);
}

export function useCanManageTemplates(): boolean {
  const { data: userRole } = useUserRole();
  return permissionService.canManageTemplates(userRole?.role);
}

export function useIsAdmin(): boolean {
  const { data: userRole } = useUserRole();
  return permissionService.isAdmin(userRole?.role);
}
